/**
 * @fileoverview World Place V3 schema definitions for locations and buildings.
 *
 * This file contains:
 * - Canonical field definitions for world places (single source of truth)
 * - Dynamically generated Zod schemas with type-safe casting
 * - All place-related schemas (entity, snapshot, idea, context variants)
 *
 * Pattern follows the character domain (character-sub-entities-v2.ts) and world-object.ts.
 */

import { z } from 'zod';
import { SHARED_WORLD_ELEMENT_FIELDS, type WorldElementFieldDefinition } from './shared-fields';

// =============================================================================
// CANONICAL FIELD DEFINITIONS - SINGLE SOURCE OF TRUTH
// =============================================================================

/**
 * Place-specific field definitions.
 * Combined with shared fields, these define all attributes of a world place.
 */
const PLACE_SPECIFIC_FIELDS: WorldElementFieldDefinition[] = [
  {
    key: 'terrain',
    label: 'Terrain & Geography',
    schemaDescription: "Physical landscape and geographic features",
    initialStateDescription: "The physical landscape and geographic features of this place.",
    journeyDescription: "The current physical landscape and any changes to the geography.",
  },
  {
    key: 'climate',
    label: 'Climate & Weather',
    schemaDescription: "Typical weather patterns and climate conditions",
    initialStateDescription: "The typical weather patterns and climate conditions.",
    journeyDescription: "Current weather patterns and any climate changes observed.",
  },
  {
    key: 'inhabitants',
    label: 'Inhabitants',
    schemaDescription: "Who or what lives here",
    initialStateDescription: "Who or what lives in this place at the start of the story.",
    journeyDescription: "Current inhabitants and any population changes.",
  },
  {
    key: 'pointsOfInterest',
    label: 'Points of Interest',
    schemaDescription: "Notable features or locations within",
    initialStateDescription: "Notable features, landmarks, or locations within this place.",
    journeyDescription: "Known points of interest and any newly discovered features.",
  },
  {
    key: 'dangers',
    label: 'Dangers & Hazards',
    schemaDescription: "Threats and risks present in this place",
    initialStateDescription: "Known threats and risks present in this place.",
    journeyDescription: "Current dangers and any new hazards that have emerged.",
  },
  {
    key: 'resources',
    label: 'Resources',
    schemaDescription: "Valuable materials or assets found here",
    initialStateDescription: "Valuable materials, assets, or resources found in this place.",
    journeyDescription: "Available resources and any changes to resource availability.",
  },
  {
    key: 'accessibility',
    label: 'Accessibility',
    schemaDescription: "How to reach this place and any barriers",
    initialStateDescription: "How to reach this place and any barriers to entry.",
    journeyDescription: "Current accessibility and any changes to routes or barriers.",
  },
  {
    key: 'atmosphere',
    label: 'Atmosphere & Mood',
    schemaDescription: "The feeling or ambiance of this place",
    initialStateDescription: "The overall feeling, mood, or ambiance of this place.",
    journeyDescription: "The current atmosphere and how it has changed through events.",
  },
];

/**
 * Complete field definitions for world places.
 * Combines shared fields with place-specific fields.
 */
export const WORLD_PLACE_FIELD_DEFINITIONS: readonly WorldElementFieldDefinition[] = [
  ...SHARED_WORLD_ELEMENT_FIELDS,
  ...PLACE_SPECIFIC_FIELDS,
] as const;

// =============================================================================
// ENUMS
// =============================================================================

/**
 * Element subtypes for world places.
 * - location: Geographic areas, regions, territories (The Widow's Maw, Forest of Whispers)
 * - building: Significant structures, landmarks, monuments (Castle Blackmoor, The Great Library)
 */
export const WorldPlaceElementEnum = z.enum(['location', 'building']);
export type WorldPlaceElement = z.infer<typeof WorldPlaceElementEnum>;

// =============================================================================
// DYNAMICALLY GENERATED SCHEMAS (following character domain pattern)
// =============================================================================

/**
 * Builds the base world place attributes schema from the canonical field definitions.
 * All fields are optional for world places.
 */
const buildAttributesSchema = () => {
  const schemaShape = WORLD_PLACE_FIELD_DEFINITIONS.reduce((acc, field) => {
    acc[field.key] = z.string().optional().describe(field.schemaDescription);
    return acc;
  }, {} as Record<string, z.ZodOptional<z.ZodString>>);

  // Type cast to preserve TypeScript inference (must be kept in sync with field definitions)
  return z.object(schemaShape as {
    // Shared fields
    relevanceToPlot: z.ZodOptional<z.ZodString>;
    currentState: z.ZodOptional<z.ZodString>;
    knownInformation: z.ZodOptional<z.ZodString>;
    // Place-specific fields
    terrain: z.ZodOptional<z.ZodString>;
    climate: z.ZodOptional<z.ZodString>;
    inhabitants: z.ZodOptional<z.ZodString>;
    pointsOfInterest: z.ZodOptional<z.ZodString>;
    dangers: z.ZodOptional<z.ZodString>;
    resources: z.ZodOptional<z.ZodString>;
    accessibility: z.ZodOptional<z.ZodString>;
    atmosphere: z.ZodOptional<z.ZodString>;
  });
};

/**
 * The base schema for world place attributes, used by agents that generate or extract
 * place details. All attributes are optional.
 */
export const WorldPlaceAttributesV3Schema = buildAttributesSchema();

// =============================================================================
// ENTITY SCHEMAS
// =============================================================================

/**
 * A point-in-time snapshot of a world place's state at the end of a specific chapter.
 * Extends attributes with temporal metadata.
 */
export const WorldPlaceSnapshotV3Schema = WorldPlaceAttributesV3Schema.extend({
  id: z.string().describe("Unique identifier for this snapshot (typically matches chapterId)."),
  chapterId: z.string().describe("ID of the chapter this snapshot corresponds to."),
  chapterNumber: z.number().describe("The number of the chapter."),
  timestamp: z.string().describe("ISO timestamp of when this snapshot was created."),
  notPresentInChapter: z.boolean().optional().describe("Flag indicating this is a carry-forward snapshot."),
});

/**
 * The canonical World Place entity.
 * A container for metadata and a timeline of snapshots.
 */
export const WorldPlaceV3Schema = z.object({
  id: z.string().describe("Unique identifier for the world place."),
  name: z.string().describe("The name of the place."),
  type: z.literal('place').describe("Discriminator for world element type."),
  element: WorldPlaceElementEnum.describe("The subtype of place (location or building)."),
  initialDescription: z.string().optional().describe("Brief description of the place before detailed generation."),
  snapshots: z.array(WorldPlaceSnapshotV3Schema).describe("Complete timeline of place state across chapters."),
  latestSnapshotId: z.string().optional().describe("Quick reference to the most recent snapshot."),
  notes: z.string().optional().describe("User notes about this place."),
  mainImageId: z.string().optional().describe("Reference to the main image for this place."),
});

// =============================================================================
// IDEATION SCHEMAS
// =============================================================================

/**
 * Schema for a world place idea before it's added to the project.
 * Used during AI ideation and extraction flows.
 */
export const WorldPlaceIdeaV3Schema = z.object({
  tempId: z.string().describe("Temporary ID for UI tracking before persistence."),
  name: z.string().describe("The proposed name of the place."),
  element: WorldPlaceElementEnum.describe("The proposed subtype (location or building)."),
  initialDescription: z.string().describe("Brief description of the place concept."),
  source: z.enum(['ideation', 'extraction', 'manual']).describe("How this idea was generated."),
});

// =============================================================================
// CONTEXT SCHEMAS (for AI agents)
// =============================================================================

/**
 * Minimal reference schema for world places.
 * Used when only ID and name are needed.
 */
export const WorldPlaceRefV3Schema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.literal('place'),
});

/**
 * Basic context schema for world places.
 * Used for high-level summaries in agent context.
 */
export const WorldPlaceBasicV3Schema = z.object({
  name: z.string(),
  type: z.literal('place'),
  element: WorldPlaceElementEnum,
  initialDescription: z.string().optional(),
});

/**
 * Rich context schema for world places.
 * Includes the full entity plus the resolved latest snapshot.
 */
export const WorldPlaceContextV3Schema = WorldPlaceV3Schema.extend({
  latestSnapshot: WorldPlaceSnapshotV3Schema.optional(),
});

// =============================================================================
// TYPE INFERENCE (single source of truth from Zod schemas)
// =============================================================================

export type WorldPlaceAttributes = z.infer<typeof WorldPlaceAttributesV3Schema>;
export type WorldPlaceSnapshotV3 = z.infer<typeof WorldPlaceSnapshotV3Schema>;
export type WorldPlaceV3 = z.infer<typeof WorldPlaceV3Schema>;
export type WorldPlaceIdeaV3 = z.infer<typeof WorldPlaceIdeaV3Schema>;
export type WorldPlaceRefV3 = z.infer<typeof WorldPlaceRefV3Schema>;
export type WorldPlaceBasicV3 = z.infer<typeof WorldPlaceBasicV3Schema>;
export type WorldPlaceContextV3 = z.infer<typeof WorldPlaceContextV3Schema>;
